import { StrictMode, useEffect } from "react";
import { createInstance, MatomoProvider } from "@datapunt/matomo-tracker-react";
import * as Sentry from "@sentry/react";
import { I18nProvider } from "react-aria";
import ReactDOM from "react-dom/client";
import { toast } from "sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import type { CellData, RowData, TableFeatures } from "@tanstack/react-table";

import { AuthProvider, useAuth } from "@workspace/auth-context";
import {
	ACCESS_DENIED_MESSAGE,
	ACCESS_DENIED_TOAST_ID,
	isAccessDenied,
	isAuthExpired,
	setAuthTokenGetter,
} from "@workspace/base-api";
import { ProfileProvider, useProfileContext } from "@workspace/profile";
import { TenantProvider, useTenant } from "@workspace/tenant-context";
import { ErrorComponent } from "@workspace/ui/components/error-component";
import { NotFoundComponent } from "@workspace/ui/components/not-found-component";
import { PWAPrompt } from "@workspace/ui/components/pwa-prompt";

import reportWebVitals from "./reportWebVitals.ts";
import { routeTree } from "./routeTree.gen.ts";

import "@workspace/ui/globals.css";

const instance = createInstance({
	urlBase:
		import.meta.env.VITE_MATOMO_URL || "https://analytics.abcs.solutions/",
	siteId: import.meta.env.VITE_MATOMO_SITE_ID || 12,
	disabled: import.meta.env.DEV, // optional, false by default. Makes all tracking calls no-ops if set to true.
});

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	enabled: import.meta.env.VITE_SENTRY_ENABLED,

	integrations: [Sentry.replayIntegration()],
	// Session Replay
	replaysSessionSampleRate:
		import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE || 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Create a client
const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
	routeTree,
	context: {
		tenants: undefined!, // This will be set after we wrap the app in a TenantProvider
		auth: undefined!, // This will be set after we wrap the app in an AuthProvider
		queryClient,
		profile: undefined!, // This will be set after we wrap the app in an AuthProvider
		timeZone: undefined!, // Default timezone, can be overridden by the tenant context
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	scrollToTopSelectors: ["#main-scrollable-area", '[data-scrollable="true"]'],
	defaultPreloadStaleTime: 0,
	defaultNotFoundComponent: NotFoundComponent,
	defaultErrorComponent: ErrorComponent,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}

	interface StaticDataRouteOption {
		section?: string;
		title?: string;
	}
}
/* eslint-disable @typescript-eslint/no-unused-vars -- declaration merging: the type
   parameters must repeat react-table's own, used or not. */
declare module "@tanstack/react-table" {
	interface TableMeta<
		in out TFeatures extends TableFeatures,
		in out TData extends RowData,
	> {
		handleAction?: (
			rowIndex: number,
			action: string,
			params?: never | null,
		) => void;
	}

	interface ColumnMeta<
		in out TFeatures extends TableFeatures,
		in out TData extends RowData,
		TValue extends CellData = CellData,
	> {
		filterVariant?: "text" | "range" | "select";
	}
}
/* eslint-enable @typescript-eslint/no-unused-vars */

const defaultSidebarOpen = false;

export function InnerApp() {
	const auth = useAuth();
	const tenant = useTenant();

	const timeZone = tenant.appInstance?.timeZone || "UTC";

	useEffect(() => {
		queryClient.getQueryCache().config.onError = (error: unknown) => {
			if (isAuthExpired(error)) {
				void auth.refresh().then((ok) => {
					if (!ok) void auth.logout();
				});
				return;
			}

			if (isAccessDenied(error)) {
				toast.error(ACCESS_DENIED_MESSAGE, { id: ACCESS_DENIED_TOAST_ID });
			}
		};

		setAuthTokenGetter(() => {
			return auth.token();
		});
	}, [auth]);

	const profile = useProfileContext({
		tenant: tenant.tenant,
		userId: auth.userId,
		router,
		defaultSidebarOpen,
	});

	return (
		<I18nProvider locale={profile.dateFormatOptions.locale}>
			<ProfileProvider
				router={router}
				values={{
					...profile,
				}}
			>
				<RouterProvider
					router={router}
					context={{
						auth,
						tenants: tenant,
						profile,
						timeZone,
					}}
				/>
			</ProfileProvider>
		</I18nProvider>
	);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MP = MatomoProvider as any;

function App() {
	return (
		<MP value={instance}>
			<QueryClientProvider client={queryClient}>
				<AuthProvider router={router}>
					<TenantProvider
						defaultSidebarOpen={defaultSidebarOpen}
						router={router}
						features={["venue-management:staff-terminal"]}
					>
						<InnerApp />
						<PWAPrompt />
					</TenantProvider>
				</AuthProvider>
			</QueryClientProvider>
		</MP>
	);
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<App />
		</StrictMode>,
	);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
